import * as React from "react";
import { useParams } from "react-router-dom";
import { Document, Page } from "react-pdf";
import { Pagination } from "@jhool-io/fe-components";
import { useFetchBatchRemits } from "../../../hooks/remits.queries";
import { useFetchDocument } from "../../../../../hooks/queries";
import Navbar from "../../../../../components/Navbar/Navbar";
import ListState from "../../../../../components/ListState/ListState";
import Loader from "../../../../../shared-ui/Loader/Loader";
import { RemitSource } from "../../../types/remits.types";
import { PDF_DOCUMENT_OPTIONS } from "../../../../../lib/pdfWorker";

export default function BatchEOB() {
    // Get id from url params
    const params = useParams();
    const remitId = params.remitId || "";
    const [documentPageNumber, setDocumentPageNumber] = React.useState(1);
    const [documentNumPages, setDocumentNumPages] = React.useState<
        null | number
    >(null);

    const batchRemitData = useFetchBatchRemits({
        batch_remit_id: remitId,
    });

    const batchRemit = batchRemitData.data?.data[0];

    const batchRemitEOB = useFetchDocument(
        batchRemit?.remit_batch_eob_url as string,
        Boolean(batchRemit?.remit_batch_eob_url)
    );

    const decodedString = () => {
        if (batchRemitEOB.data?.data) {
            return Buffer.from(batchRemitEOB.data?.data, "base64").toString(
                "utf-8"
            );
        }
        return "";
    };

    const documentToDisplay = () => {
        if (
            batchRemit?.remit_source === RemitSource.ELECTRONIC &&
            batchRemit.remit_batch_eob_url &&
            batchRemitEOB.data?.data
        ) {
            return (
                <div className="whitespace-pre-wrap">
                    <pre>{decodedString()}</pre>
                </div>
            );
        }

        if (
            batchRemit?.remit_source === RemitSource.MANUAL &&
            batchRemit.remit_batch_eob_url &&
            batchRemitEOB.data?.data
        ) {
            if (/^image/.test(batchRemitEOB.data?.content_type || "")) {
                return (
                    <>
                        {batchRemitEOB.isLoading && (
                            <span className=" text-sm flex items-center justify-center font-medium h-12">
                                Loading document....
                            </span>
                        )}
                        {batchRemitEOB.error && (
                            <span className="text-danger text-sm flex items-center justify-center font-medium  h-12">
                                Could not load document at this time
                            </span>
                        )}
                        {batchRemitEOB.data && (
                            <div className="h-full w-full">
                                <img
                                    className="w-full h-full object-contain"
                                    src={`data:image/png;base64,${batchRemitEOB.data?.data}`}
                                    alt={batchRemit.remittance_batch_id}
                                />
                            </div>
                        )}
                    </>
                );
            }

            return (
                <Document
                    error={
                        <span className="text-danger text-sm flex items-center justify-center font-medium  h-12">
                            Could not load pdf at this time
                        </span>
                    }
                    loading={
                        <span className=" text-sm flex items-center justify-center font-medium h-12">
                            Loading pdf..
                        </span>
                    }
                    onLoadSuccess={({ numPages: nextNumPages }) => {
                        setDocumentNumPages(nextNumPages);
                        setDocumentPageNumber(1);
                    }}
                    onItemClick={({ pageNumber: itemPageNumber }) => {
                        setDocumentPageNumber(Number(itemPageNumber));
                    }}
                    file={`data:application/pdf;base64,${batchRemitEOB.data?.data}`}
                    options={PDF_DOCUMENT_OPTIONS}
                >
                    <Page
                        pageNumber={documentPageNumber}
                        className="!h-auto"
                        scale={2}
                        renderAnnotationLayer={false}
                    />
                    {documentNumPages && documentNumPages > 1 && (
                        <div className="mt-[--spacing-items]">
                            <Pagination
                                totalCount={documentNumPages}
                                totalPages={documentNumPages}
                                currentPage={documentPageNumber}
                                onPageChange={(page) =>
                                    setDocumentPageNumber(page)
                                }
                                isCentered
                            />
                        </div>
                    )}
                </Document>
            );
        }
        return "";
    };

    const eobToDisplay = () => {
        if (batchRemitData.isLoading) {
            return <Loader />;
        }

        if (batchRemitData.error) {
            return (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        batchRemitData.error.response?.data.message ||
                        "Cannot display remit EOB at this time. Please try again later"
                    }
                />
            );
        }

        if (!batchRemit) {
            return (
                <ListState
                    isError={false}
                    stateHelperText="Switch to the practice this remit belongs to and open the EOB again"
                    emptyMessage="This remit was not found in the practice you are currently viewing"
                />
            );
        }

        return documentToDisplay();
    };

    return (
        <>
            <Navbar title="EOB" />
            <div className="page">{eobToDisplay()}</div>
        </>
    );
}
